import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { MemberAvatar } from './equipeUi';
import { buildPlanningDayCell } from '../../utils/equipePlanningUtils';
import { countOperationalRdvOnDay } from '../../utils/equipeRdvRules';
import {
  WEEK_DAY_COLUMNS,
  getParisWeekDays,
  getParisWeekRangeLabel,
} from '../../utils/equipeWeekParis';

const WEEK_GRID_CLASS =
  'grid grid-cols-[minmax(128px,1.15fr)_repeat(7,minmax(52px,1fr))] gap-1.5';

const CELL_STYLES = {
  full: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  split: 'bg-emerald-50/80 border-emerald-200 text-emerald-800',
  exception: 'bg-violet-50 border-violet-200 text-violet-900',
  partial: 'bg-amber-50 border-amber-200 text-amber-900',
  absence: 'bg-gray-100 border-gray-200 text-gray-500',
  off: 'bg-gray-50/80 border-gray-100 text-gray-300',
};

function PlanningCell({ cell, rdvCount = 0, onClick = null }) {
  const interactive = onClick
    ? 'cursor-pointer transition hover:ring-2 hover:ring-violet-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400'
    : '';

  if (!cell || cell.type === 'off') {
    const Tag = onClick ? 'button' : 'div';
    return (
      <Tag
        type={onClick ? 'button' : undefined}
        onClick={onClick || undefined}
        className={`flex min-h-[52px] flex-col items-center justify-center rounded-lg border text-sm ${CELL_STYLES.off} ${interactive}`}
        title={onClick ? 'Détail de cette date' : 'Absent / indisponible'}
      >
        <span>—</span>
      </Tag>
    );
  }

  const style = CELL_STYLES[cell.type] || CELL_STYLES.off;
  const content =
    (cell.type === 'split' || cell.type === 'exception') && cell.lines?.length ? (
      <div className="flex flex-col gap-0.5">
        {cell.lines.map((line) => (
          <span key={line}>{line}</span>
        ))}
      </div>
    ) : (
      <span>{cell.label}</span>
    );

  const Tag = onClick ? 'button' : 'div';

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick || undefined}
      className={`flex min-h-[52px] flex-col items-center justify-center rounded-lg border px-1 py-1 text-center text-[10px] font-medium leading-tight ${style} ${interactive}`}
      title={onClick ? 'Détail de cette date' : undefined}
    >
      {content}
      {rdvCount > 0 && (
        <span className="mt-1 rounded-full bg-white/80 px-1.5 py-0.5 text-[9px] font-semibold text-violet-700 shadow-sm">
          {rdvCount} RDV
        </span>
      )}
    </Tag>
  );
}

export default function TeamWeekPlanning({
  members,
  selectedId,
  onSelectMember,
  weekOffset = 0,
  onWeekOffsetChange,
  horairesByInstructeur = {},
  absencesByInstructeur = {},
  dateExceptionsByInstructeur = {},
  rdvList = [],
  loading = false,
  onCellClick = null,
  weekViewOnly = true,
  hideSemaineBadge = false,
  hideTodayButton = false,
  title = 'Présences',
  subtitle = 'Horaires de présence — pas les rendez-vous clients',
}) {
  const [internalView, setInternalView] = useState('semaine');
  const viewMode = weekViewOnly ? 'semaine' : internalView;
  const prestataires = (members || []).filter((m) => m.isIntervenant && m.instructeurId);
  const weekDays = getParisWeekDays(weekOffset);

  const handleSelect = (memberId) => {
    onSelectMember?.(memberId, 'planning');
  };

  return (
    <div className="flex h-full flex-col rounded-2xl border border-gray-100 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-4 py-3 sm:px-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-900">{title}</h3>
            <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>
          </div>
          {weekViewOnly && !hideSemaineBadge ? (
            <span className="inline-flex rounded-lg bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700">
              Semaine
            </span>
          ) : !weekViewOnly ? (
            <div className="flex items-center rounded-xl bg-gray-100 p-1">
              {[
                { id: 'jour', label: 'Jour' },
                { id: 'semaine', label: 'Semaine' },
                { id: 'mois', label: 'Mois' },
              ].map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setInternalView(v.id)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                    viewMode === v.id
                      ? 'bg-white text-violet-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onWeekOffsetChange?.(weekOffset - 1)}
              className="rounded-lg border border-gray-200 p-1.5 text-gray-600 hover:bg-gray-50"
              aria-label="Semaine précédente"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[9rem] text-center text-sm font-semibold text-gray-800">
              {getParisWeekRangeLabel(weekOffset)}
            </span>
            <button
              type="button"
              onClick={() => onWeekOffsetChange?.(weekOffset + 1)}
              className="rounded-lg border border-gray-200 p-1.5 text-gray-600 hover:bg-gray-50"
              aria-label="Semaine suivante"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          {!hideTodayButton ? (
            <button
              type="button"
              onClick={() => onWeekOffsetChange?.(0)}
              className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-100"
            >
              Aujourd&apos;hui
            </button>
          ) : null}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-x-auto p-3 sm:p-4">
        {loading ? (
          <div className="flex h-40 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
          </div>
        ) : viewMode !== 'semaine' ? (
          <div className="flex h-40 flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50/50 px-4 text-center">
            <p className="text-sm font-medium text-gray-700">
              Vue {viewMode === 'jour' ? 'jour' : 'mois'} — bientôt disponible
            </p>
            <p className="mt-1 text-xs text-gray-500">
              La semaine reste la vue opérationnelle prioritaire.
            </p>
          </div>
        ) : prestataires.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">
            Aucune personne ne réalise de prestations pour le moment.
          </p>
        ) : (
          <div className="min-w-[600px]">
            <div className={`mb-2 ${WEEK_GRID_CLASS}`}>
              <div />
              {WEEK_DAY_COLUMNS.map((d, idx) => (
                <div
                  key={d.value}
                  className="text-center text-[10px] font-bold uppercase tracking-wide text-gray-500"
                >
                  {d.short}
                  <div className="text-[9px] font-normal normal-case text-gray-400">
                    {weekDays[idx]?.date.getDate()}
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-1">
              {prestataires.map((m) => {
                const instructeurId = m.instructeurId;
                const config = horairesByInstructeur[instructeurId];
                const absences = absencesByInstructeur[instructeurId] || [];
                const dateExceptions = dateExceptionsByInstructeur[instructeurId] || null;
                const isSelected = selectedId === m.id;
                return (
                  <div
                    key={m.id}
                    className={`${WEEK_GRID_CLASS} rounded-xl p-0.5 ${
                      isSelected ? 'bg-violet-50 ring-1 ring-violet-200' : ''
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => handleSelect(m.id)}
                      className={`flex items-center gap-2 rounded-lg px-1 py-1.5 text-left transition ${
                        isSelected ? 'bg-white shadow-sm' : 'hover:bg-gray-50'
                      }`}
                    >
                      <MemberAvatar
                        member={m}
                        size="sm"
                        className={isSelected ? 'ring-2 ring-violet-400' : ''}
                      />
                      <span className="min-w-0 truncate text-[11px] font-semibold leading-tight text-gray-800">
                        {m.prenom}
                      </span>
                    </button>
                    {WEEK_DAY_COLUMNS.map((d, idx) => {
                      const day = weekDays[idx];
                      const cell = config
                        ? buildPlanningDayCell(
                            config,
                            absences,
                            d.value,
                            day.ymd,
                            dateExceptions,
                          )
                        : { type: 'off' };
                      const rdvCount = countOperationalRdvOnDay(
                        (rdvList || []).filter((r) => r.instructeurId === instructeurId),
                        day.ymd,
                      );
                      return (
                        <PlanningCell
                          key={d.value}
                          cell={cell}
                          rdvCount={rdvCount}
                          onClick={
                            onCellClick
                              ? () => onCellClick({ member: m, day, cell, instructeurId })
                              : null
                          }
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-gray-100 px-4 py-2.5">
        <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-gray-400">
          Légende — disponibilités
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-gray-600 sm:text-xs">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded bg-emerald-200" /> Disponible
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded bg-violet-200" /> Exception horaire
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded bg-amber-200" /> Présence partielle
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded bg-gray-200" /> Absent / indisponible
          </span>
          <span className="inline-flex items-center gap-1.5 text-violet-700">
            <span className="rounded bg-violet-100 px-1 text-[9px] font-bold">3 RDV</span>
            Compteur rendez-vous (secondaire)
          </span>
        </div>
      </div>
    </div>
  );
}
